import * as React from "react";
import { Box, Grid, Typography, colors, Button } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTitle } from "../../hooks/useTitle";
import { CheckCircleOutline, WorkOutline, Settings } from "@mui/icons-material";
import { useGetProjectsQuery } from "../../services/project.service";
import { ProjectStatus } from "../../types/project";
import ProjectNewsPanel from "./ProjectNewsPanel";
import TodaysProjects from "./TodaysProjects";
import CardBase from "../../components/global/CardBase";
import StatisticCard from "../../components/dashboard/StatisticCard";
import DashboardWelcomeHeader from "../../components/dashboard/DashboardWelcomeHeader";
import BalanceCard from "../../components/dashboard/BalanceCard";
import ChartSection from "../../components/dashboard/ChartSection";
import {
  useGetTotalHourQuery,
  useGetTotalSpentQuery,
} from "../../services/report.service";
import { useChartData } from "./hooks/useChartData";
import { useCompany } from "../../hooks/useCompany";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import DashboardCustomizeLayoutModal, {
  DASHBOARD_CARD_IDS,
  DEFAULT_DASHBOARD_CARD_ORDER,
  JOB_COUNT_CARD_IDS,
  DEFAULT_JOB_COUNT_ORDER,
} from "../../components/DashboardCustomizeLayoutModal";
import {
  useGetCardPreferencesQuery,
  useUpdateCardPreferencesMutation,
  CARD_PREFERENCE_TYPE,
  CARD_PREFERENCE_PAGE,
} from "../../services/cardPreferences.service";
import { toast } from "react-toastify";
var utc = require("dayjs/plugin/utc");
var timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);

const DashboardPage = () => {
  const company = useCompany();
  const user = useCurrentUser();
  useTitle(company?.name || "Dashboard");
  dayjs.tz.guess();

  const [hourPeriod, setHourPeriod] = React.useState("daily");
  const [costPeriod, setCostPeriod] = React.useState("daily");
  const [customizeLayoutModalOpen, setCustomizeLayoutModalOpen] =
    React.useState(false);

  // Get card preferences for dashboard
  const { data: cardPreferences } = useGetCardPreferencesQuery({
    type: CARD_PREFERENCE_TYPE.CUSTOMER,
    page: CARD_PREFERENCE_PAGE.DASHBOARD,
  });
  const [updateCardPreferences] = useUpdateCardPreferencesMutation();

  const { data: dataTotalHour } = useGetTotalHourQuery(hourPeriod);
  const { data: dataTotalCost } = useGetTotalSpentQuery(costPeriod);
  const { data: dataProjects, isLoading: isLoadingDataProjects } =
    useGetProjectsQuery({
      pageSize: 200,
      sortField: `start_date`,
      sortMode: `desc`,
      statuses: "publish,scheduled,in-progress,completed,canceled",
      load: "customer,manager,location,resources.resource,resources.reportProgresses",
    });

  const inProgressCount =
    dataProjects?.data.filter(
      (project) => project.status === ProjectStatus.IN_PROGRESS
    ).length || 0;

  const publishedCount =
    dataProjects?.data.filter(
      (project) => project.status === ProjectStatus.PUBLISHED
    ).length || 0;

  const scheduledCount =
    dataProjects?.data.filter(
      (project) => project.status === ProjectStatus.SCHEDULED
    ).length || 0;

  const canceledCount =
    dataProjects?.data.filter(
      (project) => project.status === ProjectStatus.CANCELED
    ).length || 0;

  const completedCount =
    dataProjects?.data.filter(
      (project) => project.status === ProjectStatus.COMPLETED
    ).length || 0;

  const handleHourPeriodChange = (event: SelectChangeEvent) => {
    setHourPeriod(event.target.value as string);
  };

  const handleCostPeriodChange = (event: SelectChangeEvent) => {
    setCostPeriod(event.target.value as string);
  };

  const dataHour = useChartData({
    data: dataTotalHour,
    period: hourPeriod,
    valueKey: "total_hour",
  });

  const dataCost = useChartData({
    data: dataTotalCost,
    period: costPeriod,
    valueKey: "total_spent",
  });

  // Get card order and hidden cards from preferences
  // Only use dashboard card IDs, filter out any invalid IDs
  const allDashboardCardIds = Object.values(DASHBOARD_CARD_IDS);
  const cardOrder = React.useMemo(() => {
    // If cardOrder is empty array or undefined, use default
    const savedOrder = (!cardPreferences?.cardOrder || cardPreferences.cardOrder.length === 0)
      ? DEFAULT_DASHBOARD_CARD_ORDER
      : cardPreferences.cardOrder;
    // Filter to only include valid dashboard card IDs
    const validOrder = savedOrder.filter((id) => allDashboardCardIds.includes(id as any));
    // Add any missing cards from default order
    const missingCards = DEFAULT_DASHBOARD_CARD_ORDER.filter(
      (id) => !validOrder.includes(id)
    );
    return [...validOrder, ...missingCards];
  }, [cardPreferences?.cardOrder, allDashboardCardIds]);

  const hiddenCards = React.useMemo(() => {
    const savedHidden = cardPreferences?.hiddenCards || [];
    // Only include valid dashboard card IDs
    return savedHidden.filter((id) => allDashboardCardIds.includes(id as any));
  }, [cardPreferences?.hiddenCards, allDashboardCardIds]);

  const allJobCountIds = Object.values(JOB_COUNT_CARD_IDS);
  // Get jobCountOrder and hiddenJobCount from details.jobCount or legacy fields
  const jobCountOrder = React.useMemo(() => {
    const fromDetails = cardPreferences?.details?.jobCount?.jobCountOrder;
    const fromLegacy = cardPreferences?.jobCountOrder;
    const savedOrder = fromDetails || fromLegacy || DEFAULT_JOB_COUNT_ORDER;
    const validOrder = savedOrder.filter((id: string) => allJobCountIds.includes(id as any));
    const missingSections = DEFAULT_JOB_COUNT_ORDER.filter((id: string) => !validOrder.includes(id));
    return [...validOrder, ...missingSections];
  }, [cardPreferences?.details?.jobCount?.jobCountOrder, cardPreferences?.jobCountOrder, allJobCountIds]);

  const hiddenJobCount = React.useMemo(() => {
    const fromDetails = cardPreferences?.details?.jobCount?.hiddenJobCount;
    const fromLegacy = cardPreferences?.hiddenJobCount;
    const savedHidden = fromDetails || fromLegacy || [];
    return savedHidden.filter((id: string) => allJobCountIds.includes(id as any));
  }, [cardPreferences?.details?.jobCount?.hiddenJobCount, cardPreferences?.hiddenJobCount, allJobCountIds]);

  // Filter visible cards
  const visibleCards = React.useMemo(
    () => cardOrder.filter((id) => !hiddenCards.includes(id)),
    [cardOrder, hiddenCards]
  );

  const visibleJobCountSections = React.useMemo(
    () => jobCountOrder.filter((id) => !hiddenJobCount.includes(id)),
    [jobCountOrder, hiddenJobCount]
  );

  // Filter preferences to only include dashboard card IDs before sending to modal
  const filteredPreferences = React.useMemo(() => {
    if (!cardPreferences) return undefined;
    return {
      cardOrder: cardOrder, // Already filtered to only dashboard cards
      hiddenCards: hiddenCards, // Already filtered to only dashboard cards
      jobCountOrder: jobCountOrder,
      hiddenJobCount: hiddenJobCount,
    };
  }, [cardOrder, hiddenCards, jobCountOrder, hiddenJobCount, cardPreferences]);

  const handleSaveCardPreferences = async (preferences: {
    cardOrder: string[];
    hiddenCards: string[];
    jobCountOrder: string[];
    hiddenJobCount: string[];
  }) => {
    try {
      await updateCardPreferences({
        type: CARD_PREFERENCE_TYPE.CUSTOMER,
        page: CARD_PREFERENCE_PAGE.DASHBOARD,
        data: {
          cardOrder: preferences.cardOrder,
          hiddenCards: preferences.hiddenCards,
          userId: user?.id, // Send user ID for per-user preferences
          details: {
            jobCount: {
              jobCountOrder: preferences.jobCountOrder,
              hiddenJobCount: preferences.hiddenJobCount,
            },
          },
          // Legacy fields for backward compatibility
          jobCountOrder: preferences.jobCountOrder,
          hiddenJobCount: preferences.hiddenJobCount,
        },
      }).unwrap();
      toast.success("Dashboard layout saved successfully");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to save dashboard layout");
      throw error;
    }
  };

  const customizeButtonBaseSx = {
    textTransform: "none",
    borderRadius: 2,
    whiteSpace: "nowrap",
    fontWeight: 600,
    px: { xs: 2, sm: 3 },
    py: { xs: 0.75, sm: 0.9 },
    fontSize: { xs: "0.85rem", sm: "0.95rem" },
  };

  const renderCustomizeButton = (extraSx?: Record<string, any>) => (
    <Button
      variant="outlined"
      color="primary"
      startIcon={<Settings />}
      onClick={() => setCustomizeLayoutModalOpen(true)}
      sx={{ ...customizeButtonBaseSx, ...extraSx }}
    >
      Customize Layout
    </Button>
  );

  const isBalanceVisible = visibleCards.includes(DASHBOARD_CARD_IDS.balance);

  type JobMetricConfig = {
    title: string;
    value: string;
    url: string;
    color: string;
    gradientColor?: string;
    gridProps?: Record<string, any>;
  };

  const jobCountMetricConfig: Record<string, JobMetricConfig> = React.useMemo(
    () => ({
      [JOB_COUNT_CARD_IDS.openJobs]: {
        title: "Open Jobs",
        value: `${publishedCount}`,
        url: "/works?t=1",
        color: "#0288D1",
        gridProps: { xs: 4, sm: 6, md: 2.4 },
      },
      [JOB_COUNT_CARD_IDS.scheduled]: {
        title: "Scheduled",
        value: `${scheduledCount}`,
        url: "/works?t=2",
        color: "#ED6C02",
        gradientColor: colors.orange[100],
        gridProps: { xs: 4, sm: 6, md: 2.4 },
      },
      [JOB_COUNT_CARD_IDS.inProgress]: {
        title: "In-Progress",
        value: `${inProgressCount}`,
        url: "/works?t=3",
        color: "#ED6C02",
        gradientColor: colors.orange[100],
        gridProps: { xs: 4, sm: 6, md: 2.4 },
      },
      [JOB_COUNT_CARD_IDS.completed]: {
        title: "Completed",
        value: `${completedCount}`,
        url: "/works?t=4",
        color: "#2E7D32",
        gridProps: { xs: 6, sm: 6, md: 2.4 },
      },
      [JOB_COUNT_CARD_IDS.canceled]: {
        title: "Canceled",
        value: `${canceledCount}`,
        url: "/works?t=5",
        color: "#D32F2F",
        gradientColor: colors.red[100],
        gridProps: { xs: 6, sm: 6, md: 2.4 },
      },
    }),
    [
      canceledCount,
      completedCount,
      inProgressCount,
      publishedCount,
      scheduledCount,
    ]
  );

  // Helper function to render cards based on ID
  const renderCard = (cardId: string) => {
    switch (cardId) {
      case DASHBOARD_CARD_IDS.balance:
        return (
          <React.Fragment key={cardId}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                {renderCustomizeButton()}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <BalanceCard />
            </Grid>
          </React.Fragment>
        );

      case DASHBOARD_CARD_IDS.jobCount:
        return (
          <Grid item xs={12} key={cardId}>
            <CardBase
              title="Job Count"
              useGlassCard={true}
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                      : "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {visibleJobCountSections.map((sectionId) => {
                  const metric = jobCountMetricConfig[sectionId];
                  if (!metric) return null;
                  const gridProps = metric.gridProps || { xs: 6, sm: 6, md: 3 };
                  return (
                    <Grid item key={sectionId} {...gridProps}>
                      <StatisticCard
                        title={metric.title}
                        value={metric.value}
                        url={metric.url}
                        color={metric.color}
                        gradientColor={metric.gradientColor}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </CardBase>
          </Grid>
        );

      case DASHBOARD_CARD_IDS.todaysProjects:
        return (
          <Grid item xs={12} key={cardId}>
            <CardBase
              title="Work Management Quick View"
              useGlassCard={true}
              headerActions={<WorkOutline />}
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                      : "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
                "& .card-base__header": {
                  flexDirection: { xs: "row", sm: "row" },
                  alignItems: { xs: "center", sm: "center" },
                  gap: { xs: 1, sm: 2 },
                },
              }}
            >
              <TodaysProjects
                data={dataProjects?.data || []}
                isLoading={isLoadingDataProjects}
                emptyText={"No work for today"}
              />
            </CardBase>
          </Grid>
        );

      case DASHBOARD_CARD_IDS.approvalStatus:
        return (
          <Grid item xs={12} key={cardId}>
            <CardBase
              title="Approval and Completion Statuses"
              useGlassCard={true}
              headerActions={<CheckCircleOutline />}
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                      : "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
                "& .card-base__header": {
                  flexDirection: { xs: "row", sm: "row" },
                  alignItems: { xs: "center", sm: "center" },
                  gap: { xs: 1, sm: 2 },
                },
              }}
            >
              <ProjectNewsPanel
                data={dataProjects?.data || []}
                isLoading={isLoadingDataProjects}
              />
            </CardBase>
          </Grid>
        );

      case DASHBOARD_CARD_IDS.chartMetricsTitle:
        return (
          <Grid item xs={12} sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 1, sm: 2 } }} key={cardId}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  color: "text.primary",
                }}
              >
                Chart Metrics
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                Track your work hours and costs over time
              </Typography>
            </Box>
          </Grid>
        );

      case DASHBOARD_CARD_IDS.totalHours:
        return (
          <Grid item xs={12} key={cardId}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <ChartSection
                  title="Total Hours"
                  period={hourPeriod}
                  onPeriodChange={handleHourPeriodChange}
                  data={dataHour}
                  color={colors.green[500]}
                  chartId="chartHour"
                />
              </Grid>
            </Grid>
          </Grid>
        );

      case DASHBOARD_CARD_IDS.totalCost:
        return (
          <Grid item xs={12} key={cardId}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <ChartSection
                  title="Total Cost"
                  period={costPeriod}
                  onPeriodChange={handleCostPeriodChange}
                  data={dataCost}
                  color={colors.blue[500]}
                  chartId="chartCost"
                />
              </Grid>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ pb: { xs: 2, sm: 4 }, px: { xs: 0, sm: 3 } }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            <DashboardWelcomeHeader />
          </Box>
        </Grid>
        {!isBalanceVisible && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: { xs: 1, sm: 2 },
              }}
            >
              {renderCustomizeButton()}
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {visibleCards.map((cardId) => renderCard(cardId))}
          </Grid>
        </Grid>
      </Grid>
      <DashboardCustomizeLayoutModal
        open={customizeLayoutModalOpen}
        onClose={() => setCustomizeLayoutModalOpen(false)}
        preferences={filteredPreferences}
        onSave={handleSaveCardPreferences}
      />
    </Box>
  );
};
export default DashboardPage;
